import type { Difficulty } from './types';

/**
 * The API and the UI speak different difficulty vocabularies.
 *
 * Every backend catalog — scenarios and labs — stores `easy | medium | hard |
 * expert`. The frontend's `Difficulty` type has always been `beginner |
 * intermediate | advanced | expert`, which is the wording the UI shows a
 * learner. Neither is wrong; they just have to be translated in exactly one
 * place.
 *
 * They were not. `useLabs` translated, `useScenarios` did not — so a scenario
 * arrived with `difficulty: 'medium'`, every `DIFFICULTY_VARIANT[...]` lookup
 * returned `undefined`, and 11 of 12 scenario cards rendered an unstyled badge.
 * The same untranslated value would silently break any difficulty filter added
 * to that page later.
 *
 * One map, imported by both hooks, so the next catalog cannot drift.
 */
export const API_TO_UI_DIFFICULTY: Record<string, Difficulty> = {
  easy: 'beginner',
  medium: 'intermediate',
  hard: 'advanced',
  expert: 'expert',
  // Already-UI values pass through: the mock API and some older rows use them,
  // and a value that is already correct must not fall through to the default.
  beginner: 'beginner',
  intermediate: 'intermediate',
  advanced: 'advanced',
};

/** Normalize whatever the API sent into the vocabulary the UI renders. */
export function toUiDifficulty(raw: unknown): Difficulty {
  return API_TO_UI_DIFFICULTY[String(raw ?? '').trim().toLowerCase()] ?? 'intermediate';
}
